import { useEffect, useMemo, useState } from 'react';
import { FileSearchOutlined, PlusOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';

import { PageHeader } from '../../../components/shared';
import { Button, Dialog, Empty } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useKhopManHinh } from '../../../hooks/useKhopManHinh';
import { useLoi } from '../../../hooks/useLoi';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { useToast } from '../../../lib/toast';
import { CayThuVien } from '../components/CayThuVien';
import type { MucCay } from '../components/CayThuVien';
import { ChiTietTieuChi } from '../components/ChiTietTieuChi';
import { FormBoTieuChi } from '../components/FormBoTieuChi';
import { FormLinhVuc } from '../components/FormLinhVuc';
import { FormLoaiHoSo } from '../components/FormLoaiHoSo';
import { HopPhatHanh } from '../components/HopPhatHanh';
import { HopXacNhanXoa } from '../components/HopXacNhanXoa';
import { TrinhSoanBo } from '../components/TrinhSoanBo';
import { VungDuLieu } from '../components/VungDuLieu';
import { QUYEN } from '../constants';
import {
  useBoChiTiet, useCayThuVien, useLinhVuc, useLoaiHoSo, useSuaBo, useXoaBo, useXoaLinhVuc, useXoaLoaiHoSo,
} from '../hooks/useThamDinh';
import type { TieuChi } from '../types';

/** Chữ của hộp xác nhận xoá theo cấp của nút cây. */
const CHU_XOA = {
  'linh-vuc': {
    tieuDe: 'thamDinh.linhVuc.xoaHoiTieuDe', than: 'thamDinh.linhVuc.xoaHoiThan', nut: 'thamDinh.linhVuc.xoaXacNhan',
  },
  loai: {
    tieuDe: 'thamDinh.loaiHoSo.xoaHoiTieuDe', than: 'thamDinh.loaiHoSo.xoaHoiThan', nut: 'thamDinh.loaiHoSo.xoaXacNhan',
  },
  bo: { tieuDe: 'thamDinh.bo.xoaHoiTieuDe', than: 'thamDinh.bo.xoaHoiThan', nut: 'thamDinh.bo.xoaXacNhan' },
} as const satisfies Record<MucCay['cap'], Record<'tieuDe' | 'than' | 'nut', KhoaDich>>;

/**
 * Thư viện tiêu chí (`/client/tham-dinh/tieu-chi`, ADR-0080 đợt 1).
 *
 * Ba cột theo giao diện v3: cây thư viện ▸ trình soạn bộ ▸ chi tiết tiêu chí. Bố cục này phục vụ đúng việc
 * người soạn làm — nhảy giữa các bộ, sửa trọng số, mở từng tiêu chí ra viết hướng dẫn — mà ba tab tách rời
 * không làm được: sửa một tiêu chí phải nhớ nó thuộc bộ nào ở tab khác.
 *
 * Bộ đang mở giữ ở `?bo=` để tải lại trang hay gửi link cho người khác vẫn vào đúng chỗ.
 *
 * Màn hẹp (dưới `xl`) xếp dọc: cây trên, trình soạn dưới, chi tiết trong hộp thoại — ba cột 320px cạnh nhau
 * ở 768px thì cột nào cũng không đọc được.
 */
export function ThuVienTieuChiView() {
  const { t } = useT();
  const q = useModulePermissions(QUYEN);
  const dieuHuong = useDieuHuong();
  const [thamSo, setThamSo] = useSearchParams();
  const boId = thamSo.get('bo') ?? undefined;

  const [chonTieuChi, setChonTieuChi] = useState<string | undefined>();
  const [formLinhVuc, setFormLinhVuc] = useState(false);
  const [formLoai, setFormLoai] = useState(false);
  const [formBo, setFormBo] = useState(false);
  const [moPhatHanh, setMoPhatHanh] = useState(false);
  const [moChiTiet, setMoChiTiet] = useState(false);
  // Mục đang sửa / sắp xoá từ menu `⋮` của cây. Giữ cả nút trong state để object đưa vào form ổn định giữa
  // các lần render — form nạp lại giá trị mỗi khi `dangSua` đổi tham chiếu.
  const [sua, setSua] = useState<MucCay | undefined>();
  const [xoa, setXoa] = useState<MucCay | undefined>();
  // Mốc `xl` của Tailwind, khớp class ẩn/hiện cột phải bên dưới.
  const manRong = useKhopManHinh('(min-width: 1280px)');

  const cay = useCayThuVien();
  const chiTiet = useBoChiTiet(boId);
  // Hai danh mục cho ô chọn của form; cây không mang đủ trường để tạo bộ mới.
  const dsLinhVuc = useLinhVuc({ limit: 100 });
  const dsLoai = useLoaiHoSo({ limit: 100 });

  const toast = useToast();
  const dichLoi = useLoi();
  const suaBo = useSuaBo();
  const xoaLinhVuc = useXoaLinhVuc();
  const xoaLoai = useXoaLoaiHoSo();
  const xoaBo = useXoaBo();
  const dangXoa = xoa?.cap === 'linh-vuc' ? xoaLinhVuc : xoa?.cap === 'loai' ? xoaLoai : xoaBo;

  // Nút cây không mang id cha; ghép vào đây, một lần cho mỗi lượt mở form.
  const suaLoai = useMemo(
    () => (sua?.cap === 'loai' ? { ...sua.nut, linh_vuc_id: sua.linhVucId } : undefined),
    [sua],
  );
  const suaVoBo = useMemo(
    () => (sua?.cap === 'bo' ? { ...sua.nut, loai_ho_so_id: sua.loaiId } : undefined),
    [sua],
  );

  function dongXoa() {
    setXoa(undefined);
    dangXoa.reset();
  }

  function xacNhanXoa() {
    if (!xoa) return;
    const du = { id: xoa.nut.id, ten: xoa.nut.ten };
    if (xoa.cap === 'linh-vuc') xoaLinhVuc.mutate(du, { onSuccess: dongXoa });
    else if (xoa.cap === 'loai') xoaLoai.mutate(du, { onSuccess: dongXoa });
    else {
      xoaBo.mutate(du, {
        onSuccess: () => {
          // Bộ vừa xoá đang mở ở cột giữa thì đóng lại, kẻo cột giữa gọi chi tiết một bộ không còn và báo 404.
          if (du.id === boId) {
            setThamSo((cu) => {
              const moi = new URLSearchParams(cu);
              moi.delete('bo');
              return moi;
            }, { replace: true });
          }
          dongXoa();
        },
      });
    }
  }

  function chonBo(id: string) {
    setThamSo((cu) => {
      const moi = new URLSearchParams(cu);
      moi.set('bo', id);
      return moi;
    }, { replace: true });
    setChonTieuChi(undefined);
  }

  // Đổi bộ thì bỏ tiêu chí đang chọn: mã tiêu chí chỉ có nghĩa trong một bộ.
  useEffect(() => setChonTieuChi(undefined), [boId]);

  const bo = chiTiet.data;
  const tieuChiDangChon: TieuChi | undefined = useMemo(() => {
    if (!bo?.ban_nhap || !chonTieuChi) return undefined;
    for (const n of bo.ban_nhap.nhom) {
      const tc = n.tieu_chi.find((x) => x.ma === chonTieuChi);
      if (tc) return tc;
    }
    return undefined;
  }, [bo, chonTieuChi]);

  /**
   * Sửa một tiêu chí từ cột phải. Trình soạn giữ bản nháp trong state của nó, nên ở đây chỉ phát tín hiệu
   * — `TrinhSoanBo` nhận qua `onSuaTieuChi` và tự ghi vào cây của mình.
   */
  const [suaTieuChi, setSuaTieuChi] = useState<((f: (tc: TieuChi) => void) => void) | undefined>();

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.thamDinhAi') }, { label: t('nav.muc.thuVienTieuChi') }]}
          titleIcon={<FileSearchOutlined className="text-primary-600" />}
          title={t('thamDinh.tieuChiTieuDe')}
          subtitle={t('thamDinh.tieuChiMo')}
          actions={
            <>
              <Button
                variant="ghost"
                size="md"
                title={t('chung.lamMoi')}
                aria-label={t('chung.lamMoi')}
                onClick={() => { void cay.refetch(); void chiTiet.refetch(); }}
              >
                <ReloadOutlined className={cay.isFetching ? 'animate-spin' : ''} />
              </Button>
              {q.danhMucTao && (
                <Button variant="outline" size="md" icon={<SettingOutlined />} onClick={() => setFormLoai(true)}>
                  {t('thamDinh.loaiHoSo.themMoi')}
                </Button>
              )}
              {q.boTao && (
                <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setFormBo(true)}>
                  {t('thamDinh.bo.themMoi')}
                </Button>
              )}
            </>
          }
        />

        <VungDuLieu
          dangTai={cay.isPending}
          loi={cay.error}
          dulieu={cay.data}
          rong={false}
          onThuLai={() => void cay.refetch()}
          rongTieuDe={t('thamDinh.cay.rong')}
        >
          {(duCay) => (
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 xl:col-span-3">
                <div className="h-[32rem] xl:h-[calc(100vh-14rem)]">
                  <CayThuVien
                    cay={duCay}
                    dangTai={cay.isFetching && duCay.length === 0}
                    dangChon={boId}
                    duocThem={q.danhMucTao}
                    quyen={q}
                    onChonBo={chonBo}
                    onThemLinhVuc={() => setFormLinhVuc(true)}
                    onSua={setSua}
                    onXoa={setXoa}
                    onDoiTrangThaiBo={(b, trangThai) =>
                      suaBo.mutate(
                        { id: b.id, du: { trang_thai: trangThai } },
                        // Chạy thẳng từ menu, không có form để đặt lỗi cạnh chỗ sai — toast là chỗ duy nhất.
                        { onError: (e) => toast.error(dichLoi(e)) },
                      )}
                  />
                </div>
              </div>

              <div className="col-span-12 xl:col-span-6">
                <div className="h-[36rem] xl:h-[calc(100vh-14rem)]">
                  {!boId ? (
                    <div className="flex h-full items-center rounded-lg border border-default bg-surface shadow-sm">
                      <Empty
                        icon={FileSearchOutlined}
                        title={t('thamDinh.soan.chuaChonBo')}
                        description={t('thamDinh.soan.chuaChonBoMo')}
                      />
                    </div>
                  ) : (
                    <VungDuLieu
                      dangTai={chiTiet.isPending}
                      loi={chiTiet.error}
                      dulieu={bo}
                      rong={false}
                      onThuLai={() => void chiTiet.refetch()}
                      rongTieuDe={t('thamDinh.soan.chuaChonBo')}
                    >
                      {(du) => (
                        <TrinhSoanBo
                          bo={du}
                          duocSua={q.boSua}
                          dangChon={chonTieuChi}
                          onChonTieuChi={(ma) => { setChonTieuChi(ma); if (ma) setMoChiTiet(true); }}
                          onDangKySua={setSuaTieuChi}
                          onPhatHanh={() => setMoPhatHanh(true)}
                          onChayThu={(phienId) => dieuHuong(`/tham-dinh/ho-so/${phienId}`)}
                        />
                      )}
                    </VungDuLieu>
                  )}
                </div>
              </div>

              {/* Cột phải chỉ hiện ở màn rộng; màn hẹp dùng hộp thoại bên dưới. */}
              <div className="hidden xl:col-span-3 xl:block">
                <div className="h-[calc(100vh-14rem)]">
                  <ChiTietTieuChi
                    tieuChi={tieuChiDangChon}
                    duocSua={q.boSua}
                    onDoi={(f) => suaTieuChi?.(f)}
                  />
                </div>
              </div>
            </div>
          )}
        </VungDuLieu>
      </div>

      {/* Màn hẹp: chi tiết tiêu chí trong hộp thoại, vì ba cột cạnh nhau ở 768px không đọc được. Màn rộng đã
          có cột phải nên không mở — ẩn bằng `xl:hidden` thì mask của antd vẫn phủ kín màn. */}
      <Dialog
        open={!manRong && moChiTiet && Boolean(tieuChiDangChon)}
        onClose={() => setMoChiTiet(false)}
        title={t('thamDinh.chiTiet.tieuDe')}
      >
        <div className="h-[60vh] pt-2">
          <ChiTietTieuChi tieuChi={tieuChiDangChon} duocSua={q.boSua} onDoi={(f) => suaTieuChi?.(f)} />
        </div>
      </Dialog>

      <FormLinhVuc open={formLinhVuc} onClose={() => setFormLinhVuc(false)} />
      <FormLoaiHoSo open={formLoai} linhVuc={dsLinhVuc.data?.items ?? []} onClose={() => setFormLoai(false)} />
      <FormBoTieuChi open={formBo} loaiHoSo={dsLoai.data?.items ?? []} onClose={() => setFormBo(false)} />

      <FormLinhVuc
        open={sua?.cap === 'linh-vuc'}
        dangSua={sua?.cap === 'linh-vuc' ? sua.nut : undefined}
        onClose={() => setSua(undefined)}
      />
      <FormLoaiHoSo
        open={sua?.cap === 'loai'}
        dangSua={suaLoai}
        linhVuc={dsLinhVuc.data?.items ?? []}
        onClose={() => setSua(undefined)}
      />
      <FormBoTieuChi
        open={sua?.cap === 'bo'}
        dangSua={suaVoBo}
        loaiHoSo={dsLoai.data?.items ?? []}
        onClose={() => setSua(undefined)}
      />

      <HopXacNhanXoa
        open={Boolean(xoa)}
        onClose={dongXoa}
        tieuDe={t(CHU_XOA[xoa?.cap ?? 'bo'].tieuDe)}
        than={t(CHU_XOA[xoa?.cap ?? 'bo'].than, { ten: xoa?.nut.ten ?? '' })}
        nhanXacNhan={t(CHU_XOA[xoa?.cap ?? 'bo'].nut)}
        dangChay={dangXoa.isPending}
        loi={dangXoa.error}
        onXacNhan={xacNhanXoa}
      />
      {bo && <HopPhatHanh open={moPhatHanh} bo={bo} onClose={() => setMoPhatHanh(false)} />}
    </div>
  );
}
